import { PAGES, GALLERIES, COMPANY_INFO, HERO_SLIDES } from "../data";
import { LOCAL_PAGES } from "../localPagesData";

/**
 * Universal helper that attempts relative same-origin endpoint first,
 * falling back to absolute dev endpoint or null.
 */
async function fetchFromCms(collectionName) {
  // 1. Try relative same-origin endpoint
  try {
    const res = await fetch(`/_api/public/content/${collectionName}`);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        return data;
      }
    }
  } catch {
    // Relative fetch failed, continue to fallback
  }

  // 2. Try absolute origin endpoint
  try {
    const res = await fetch(`https://api.dev.auxo.dk/public/content/${collectionName}`);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        return data;
      }
    }
  } catch {
    // Absolute fetch failed
  }

  return null;
}

/**
 * Normalizes a localized string or plain string to { da: string }.
 */
function toLocalized(val, fallback = "") {
  if (!val) return { da: fallback };
  if (typeof val === "object" && val !== null) {
    return { da: val.da || val.en || fallback };
  }
  return { da: String(val) };
}

/**
 * Fetches company information from the 'virksomhedsoplysninger' collection.
 */
export async function fetchCompanyInfo() {
  try {
    const items = await fetchFromCms("virksomhedsoplysninger");
    if (items && items[0]) {
      const item = items[0];
      const phone = item.telefon || COMPANY_INFO.phone;
      const phoneRaw = phone ? `+45${phone.replace(/\s+/g, "")}` : COMPANY_INFO.phoneRaw;
      return {
        name: item.firmanavn || COMPANY_INFO.name,
        address: item.adresse || COMPANY_INFO.address,
        cvr: item.cvr || COMPANY_INFO.cvr,
        phone,
        phoneDisplay: phone,
        phoneRaw,
        email: item.email || COMPANY_INFO.email,
        experience: item.erfaring || COMPANY_INFO.experience,
        description: item.kort_beskrivelse || "Din lokale tømrer i Aarhus Syd. Vi leverer solidt håndværk, personlig kontakt og høj kvalitet til dit næste byggeprojekt.",
        logo: item.logo?.[0] || COMPANY_INFO.logo,
        bygGarantiLogo: item.byg_garanti_logo?.[0] || COMPANY_INFO.bygGarantiLogo,
        diByggeriLogo: item.di_byggeri_logo?.[0] || COMPANY_INFO.diByggeriLogo,
        favicon: COMPANY_INFO.favicon,
      };
    }
  } catch (error) {
    console.warn("fetchCompanyInfo failed, using local fallback:", error);
  }
  return COMPANY_INFO;
}

/**
 * Fetches all pages from the 'sider' collection.
 * Falls back to 'pages' or local data on failure.
 */
export async function fetchPages() {
  try {
    // Try Danish collection 'sider'
    let items = await fetchFromCms("sider");

    // Fallback to old 'pages' if 'sider' is not yet populated
    if (!items) {
      items = await fetchFromCms("pages");
    }

    if (Array.isArray(items) && items.length > 0) {
      return items.map((item) => {
        const sideId = item.side_id || item.page_id;
        const gallerySlug =
          item.terms?.["galleri-reference"]?.[0] ||
          item.gallery_slug ||
          (item.tilknyttet_galleri && item.tilknyttet_galleri !== "ingen" ? item.tilknyttet_galleri : null);

        return {
          id: item.id,
          side_id: sideId,
          page_id: sideId,
          title: toLocalized(item.titel || item.title),
          meta_title: toLocalized(item.meta_titel || item.meta_title),
          meta_description: toLocalized(item.meta_beskrivelse || item.meta_description),
          content: toLocalized(item.indhold || item.content),
          udvalgt_billede: item.udvalgt_billede?.[0] || item.og_image || null,
          og_image: item.udvalgt_billede?.[0] || item.og_image || null,
          gallery_slug: gallerySlug,
          terms: item.terms,
        };
      });
    }
  } catch (error) {
    console.warn("fetchPages failed, using local fallback:", error);
  }

  // Local fallback
  return Object.values(PAGES).map((page) => ({
    side_id: page.page_id,
    page_id: page.page_id,
    title: { da: page.title },
    content: { da: page.content },
    meta_title: { da: page.meta_title },
    meta_description: { da: page.meta_description },
    og_image: page.og_image,
    gallery_slug: page.gallery_slug,
    faq: page.faq,
    schema_markup: page.schema_markup,
  }));
}

/**
 * Fetches a single page by side_id / page_id.
 */
export async function fetchPageById(pageId) {
  try {
    const pages = await fetchPages();
    const page = pages.find((p) => p.side_id === pageId || p.page_id === pageId);
    if (page) {
      // If page has a local rich FAQ or schema, preserve it as default
      const local = PAGES[pageId] || (LOCAL_PAGES && LOCAL_PAGES[pageId]);
      if (local?.faq) page.faq = local.faq;
      if (local?.schema_markup) page.schema_markup = local.schema_markup;

      // Also try to enrich with dynamic CMS FAQ if applicable
      const categoryMap = {
        totalrenoveringer: "totalrenovering",
        totalrenovering: "totalrenovering",
        ombygninger: "ombygning",
        ombygning: "ombygning",
        totalentrepriser: "totalentreprise",
        totalentreprise: "totalentreprise",
      };
      if (categoryMap[pageId]) {
        const dynamicFaq = await fetchFaq(categoryMap[pageId]);
        if (dynamicFaq && dynamicFaq.length > 0) {
          page.faq = {
            section_heading: page.faq?.section_heading || "Ofte stillede spørgsmål",
            items: dynamicFaq,
          };
        }
      }

      return page;
    }
  } catch (error) {
    console.warn(`Error fetching page ${pageId}:`, error);
  }

  // Check local data
  const local = PAGES[pageId];
  if (local) {
    return {
      side_id: local.page_id,
      page_id: local.page_id,
      title: { da: local.title },
      content: { da: local.content },
      meta_title: { da: local.meta_title },
      meta_description: { da: local.meta_description },
      og_image: local.og_image,
      gallery_slug: local.gallery_slug,
      faq: local.faq,
      schema_markup: local.schema_markup,
    };
  }

  // Check local landing pages
  const localLanding = LOCAL_PAGES?.[pageId];
  if (localLanding) {
    return {
      side_id: localLanding.slug,
      page_id: localLanding.slug,
      title: { da: localLanding.h1 },
      content: { da: localLanding.intro },
      meta_title: { da: localLanding.seo?.title_tag },
      meta_description: { da: localLanding.seo?.meta_description },
      gallery_slug: "renovering",
      faq: localLanding.faq,
      schema_markup: localLanding.schema_markup,
    };
  }

  return null;
}

/**
 * Fetches all galleries from the 'gallerier' collection.
 * Falls back to 'galleries' or local data on failure.
 */
export async function fetchGalleries() {
  try {
    // Try Danish collection 'gallerier'
    let items = await fetchFromCms("gallerier");

    // Fallback to old 'galleries'
    if (!items) {
      items = await fetchFromCms("galleries");
    }

    if (Array.isArray(items) && items.length > 0) {
      return items.map((item) => ({
        id: item.id,
        gallery_id: item.galleri_id || item.gallery_id,
        galleri_id: item.galleri_id || item.gallery_id,
        title: toLocalized(item.titel || item.title),
        images: item.billeder || item.images || [],
        billeder: item.billeder || item.images || [],
        terms: item.terms,
      }));
    }
  } catch (error) {
    console.warn("fetchGalleries failed, using local fallback:", error);
  }

  // Local fallback
  return Object.values(GALLERIES).map((gallery) => ({
    gallery_id: gallery.gallery_id,
    galleri_id: gallery.gallery_id,
    title: { da: gallery.title },
    images: gallery.images,
    billeder: gallery.images,
  }));
}

/**
 * Fetches a single gallery by gallery_id.
 */
export async function fetchGalleryById(galleryId) {
  try {
    const galleries = await fetchGalleries();
    const gallery = galleries.find(
      (g) => g.gallery_id === galleryId || g.galleri_id === galleryId
    );
    if (gallery) return gallery;
  } catch (error) {
    console.warn(`Error fetching gallery ${galleryId}:`, error);
  }

  const local = GALLERIES[galleryId];
  if (local) {
    return {
      gallery_id: local.gallery_id,
      galleri_id: local.gallery_id,
      title: { da: local.title },
      images: local.images,
      billeder: local.images,
    };
  }
  return null;
}

/**
 * Fetches services from the 'ydelser' collection.
 */
export async function fetchServices() {
  try {
    const items = await fetchFromCms("ydelser");
    if (Array.isArray(items) && items.length > 0) {
      return items
        .sort((a, b) => (Number(a.sortering) || 0) - (Number(b.sortering) || 0))
        .map((item) => ({
          title: item.titel?.da || item.titel || "",
          path: item.sti || "",
          slug: item.sti?.replace(/^\/ydelser\//, "")?.replace(/^\//, "") || "",
          desc: item.kort_beskrivelse?.da || item.kort_beskrivelse || "",
          image: item.billede?.[0] || "",
          highlight: Boolean(item.fremhaevet_forside),
          sortering: Number(item.sortering) || 0,
        }));
    }
  } catch (error) {
    console.warn("fetchServices failed:", error);
  }
  return null;
}

/**
 * Fetches hero slides from the 'forside_slides' collection.
 */
export async function fetchHeroSlides() {
  try {
    const items = await fetchFromCms("forside_slides");
    if (Array.isArray(items) && items.length > 0) {
      return items
        .sort((a, b) => (Number(a.sortering) || 0) - (Number(b.sortering) || 0))
        .map((item) => ({
          title: item.titel?.da || item.titel || "",
          subtitle: item.undertitel?.da || item.undertitel || "",
          image: item.billede?.[0] || "",
          sortering: Number(item.sortering) || 0,
        }));
    }
  } catch (error) {
    console.warn("fetchHeroSlides failed:", error);
  }
  return HERO_SLIDES;
}

/**
 * Fetches company benefits from the 'fordele' collection.
 */
export async function fetchBenefits() {
  try {
    const items = await fetchFromCms("fordele");
    if (Array.isArray(items) && items.length > 0) {
      return items
        .sort((a, b) => (Number(a.sortering) || 0) - (Number(b.sortering) || 0))
        .map((item) => ({
          title: item.titel?.da || item.titel || "",
          description: item.beskrivelse?.da || item.beskrivelse || "",
          sortering: Number(item.sortering) || 0,
        }));
    }
  } catch (error) {
    console.warn("fetchBenefits failed:", error);
  }
  return null;
}

/**
 * Fetches FAQ questions from the 'faq_spoergsmaal' collection for a given category.
 */
export async function fetchFaq(categorySlug) {
  try {
    const items = await fetchFromCms("faq_spoergsmaal");
    if (Array.isArray(items) && items.length > 0) {
      const filtered = items.filter((item) => {
        const terms = item.terms?.["faq-side"] || [];
        return terms.includes(categorySlug);
      });
      if (filtered.length > 0) {
        return filtered
          .sort((a, b) => (Number(a.sortering) || 0) - (Number(b.sortering) || 0))
          .map((item) => {
            const rawAnswer = item.svar?.da || item.svar || "";
            const cleanAnswer = rawAnswer.replace(/^<p>/, "").replace(/<\/p>$/, "");
            return {
              question: item.spoergsmaal?.da || item.spoergsmaal || "",
              answer: cleanAnswer,
            };
          });
      }
    }
  } catch (error) {
    console.warn(`fetchFaq for ${categorySlug} failed:`, error);
  }
  return null;
}
